<template>
  <div id="new-shipper">
    <BreadCrumb :items="breadcrumbItems" />

    <v-card elevation="1" class="pt-5 mt-1">
      <template v-if="isShipperLoading">
        <div class="py-5 px-5 mt-1 text-center">
          Aguarde, buscando dados
          <div class="mt20"></div>
          <v-progress-linear indeterminate class="mb-0"></v-progress-linear>
        </div>
      </template>
      <template v-else>
        <div class="header d-flex justify-space-between pr-5 align-center pb-5">
          <h1>{{ pageTitle }}</h1>
        </div>
        <v-form @submit.prevent="submit" v-model="isFormValid" :key="forceUpdate">
          <v-row>
            <v-col cols="12">
              <div class="divider"></div>
            </v-col>
          </v-row>

          <v-row class="px-9">
            <v-col cols="12">
              <v-radio-group
                v-model="selectedType"
                :disabled="isViewOnly"
                @change="forceUpdateToRule()"
                row
                hide-details
                class="py-0 my-0"
              >
                <template #label> Tipo do Embarcador: </template>
                <v-radio label="Empresa" value="company"></v-radio>
                <v-radio label="Pessoal" value="personal"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <div class="divider"></div>
            </v-col>
          </v-row>

          <v-row class="px-9">
            <v-col cols="12">
              <h6>Dados Gerais</h6>
            </v-col>

            <v-col v-show="isCompanyShipper" cols="12" md="4">
              <v-text-field
                v-model="generalData.socialReason"
                :disabled="isViewOnly"
                label="Razão Social (Obrigatório)"
                :rules="[rules.companyRequired]"
                autocomplete="off"
              ></v-text-field>
            </v-col>

            <v-col v-show="isCompanyShipper" cols="12" md="4">
              <v-text-field
                v-model="generalData.fantasyName"
                :disabled="isViewOnly"
                :rules="[rules.companyRequired]"
                label="Nome Fantasia (Obrigatório)"
                required
              ></v-text-field>
            </v-col>

            <v-col v-show="isCompanyShipper" cols="12" md="4">
              <v-combobox
                v-model="generalData.cnpj"
                :disabled="isViewOnly"
                v-mask="'BB.BBB.BBB/BBBB-BB'"
                :rules="[rules.cnpjRequired]"
                :search-input.sync="cnpjInputText"
                append-icon=""
                :delimiters="[' ']"
                label="CNPJ (Obrigatório)"
                hide-selected
                multiple
                chips
                deletable-chips
              ></v-combobox>
            </v-col>

            <v-col v-show="!isCompanyShipper" cols="12" md="4">
              <v-text-field
                v-model="generalData.personalName"
                :disabled="isViewOnly"
                label="Nome (Obrigatório)"
                :rules="[rules.personalRequired]"
                required
                autocomplete="off"
              ></v-text-field>
            </v-col>

            <v-col v-show="!isCompanyShipper" cols="12" md="4">
              <v-combobox
                v-model="generalData.cpf"
                :disabled="isViewOnly"
                v-mask="'BBB.BBB.BBB-BB'"
                :rules="[rules.cpfRequired]"
                :search-input.sync="cpfInputText"
                append-icon=""
                :delimiters="[' ']"
                label="CPF (Obrigatório)"
                hide-selected
                multiple
                chips
                deletable-chips
              ></v-combobox>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="generalData.celphone"
                :disabled="isViewOnly"
                label="Celular"
                required
                autocomplete="off"
                v-mask="'(##) #####-####'"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="generalData.phone"
                :disabled="isViewOnly"
                label="Telefone"
                required
                autocomplete="off"
                v-mask="'(##) ####-####'"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="generalData.email"
                :disabled="isViewOnly"
                label="Email (Obrigatório)"
                :rules="[rules.required]"
                type="email"
                autocomplete="off"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <div class="divider"></div>
            </v-col>
          </v-row>

          <v-row class="px-9">
            <v-col cols="12">
              <h6>Endereço</h6>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="addressData.cep"
                :disabled="isViewOnly"
                label="CEP"
                required
                autocomplete="off"
                v-mask="'#####-###'"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="addressData.stateId"
                :disabled="isViewOnly"
                item-text="name"
                item-value="id"
                label="Estado"
                :items="states"
                required
                autocomplete="off"
                @change="getCities"
                clearable
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="addressData.cityId"
                :disabled="isViewOnly || !cities.length"
                item-text="name"
                item-value="id"
                label="Cidade"
                :items="cities"
                required
                autocomplete="off"
                clearable
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="addressData.neighborhood"
                :disabled="isViewOnly"
                label="Bairro"
                required
                autocomplete="off"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="addressData.street"
                :disabled="isViewOnly"
                label="Logradouro"
                required
                autocomplete="off"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="addressData.number"
                :disabled="isViewOnly"
                label="Número"
                required
                autocomplete="off"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="addressData.complement"
                :disabled="isViewOnly"
                label="Complemento"
                required
                autocomplete="off"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <div class="divider"></div>
            </v-col>
          </v-row>

          <v-row class="px-9">
            <v-col cols="12">
              <h6>Dados de Acesso</h6>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="generalData.email"
                :disabled="isViewOnly"
                readonly
                label="Email"
                required
                autocomplete="off"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="accessData.password"
                :disabled="isViewOnly"
                :rules="[rules.required]"
                type="password"
                label="Senha (Obrigatório)"
                autocomplete="off"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                ref="confirmPasswordRef"
                v-model="accessData.confirmPassword"
                :disabled="isViewOnly"
                :rules="[rules.confirmPassword]"
                type="password"
                label="Confirmar Senha (Obrigatório)"
                autocomplete="off"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="px-9 pb-2">
            <v-col cols="12" class="d-flex justify-end" style="gap: 12px">
              <v-btn large :color="backButtonColor" @click="cancel()"> {{ backButtonLabel }} </v-btn>
              <v-btn
                v-if="!isViewOnly"
                :loading="isSubmitButtonLoading"
                :disabled="!isFormValid"
                large
                color="success"
                @click="onSubmit()"
              >
                SALVAR
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
        <MessagesError :errors="errors" :type="errorsType" @closeMessages="errors = []" />
      </template>
    </v-card>
  </div>
</template>

<script>
import BreadCrumb from '@/components/BreadCrumb.vue';
import ShipperTypes from '@/consts/ShipperType.js';
import { mapActions } from 'vuex';
import MessagesError from '@/components/MessagesError.vue';
export default {
  name: 'NewShipper',
  components: {
    BreadCrumb,
    MessagesError
  },
  data: () => ({
    errors: [],
    isShipperLoading: false,
    errorsType: 'error',
    states: [],
    cities: [],
    isSubmitButtonLoading: false,
    cnpjInputText: '',
    cpfInputText: '',
    forceUpdate: 0,
    ShipperTypes,
    breadcrumbItems: [
      {
        text: 'Início',
        disabled: false,
        to: '/start'
      },
      {
        text: 'Embarcadores',
        disabled: false,
        to: '/shippers',
        exact: true
      },
      {
        text: 'Novo embarcador',
        disabled: true,
        to: '/shippers/new'
      }
    ],
    isFormValid: false,
    selectedType: ShipperTypes.COMPANY,
    generalData: {
      email: '',
      personalName: '',
      socialReason: '',
      fantasyName: '',
      cnpj: [],
      cpf: [],
      celphone: '',
      phone: ''
    },
    addressData: {
      cep: '',
      stateId: '',
      cityId: '',
      neighborhood: '',
      number: '',
      complement: ''
    },
    accessData: {
      password: '',
      confirmPassword: ''
    }
  }),

  watch: {
    'accessData.password': {
      handler() {
        this.$refs.confirmPasswordRef.validate();
      }
    }
  },

  computed: {
    isCompanyShipper() {
      return this.selectedType === ShipperTypes.COMPANY;
    },
    isConfirmPasswordValid() {
      return !!this.accessData.confirmPassword && this.accessData.password === this.accessData.confirmPassword;
    },
    rules() {
      return {
        required: (value) => !!value || 'Required.',
        confirmPassword: () => {
          return this.isConfirmPasswordValid;
        },
        cnpjRequired: (value) => {
          if (this.selectedType === ShipperTypes.COMPANY) {
            return !!value.length || 'Required.';
          } else {
            return true;
          }
        },
        cpfRequired: (value) => {
          if (this.selectedType === ShipperTypes.PERSONAL) {
            return !!value.length || 'Required.';
          } else {
            return true;
          }
        },
        companyRequired: (value) => {
          if (this.selectedType === ShipperTypes.COMPANY) {
            return !!value || 'Required.';
          } else {
            return true;
          }
        },
        personalRequired: (value) => {
          if (this.selectedType === ShipperTypes.PERSONAL) {
            return !!value || 'Required.';
          } else {
            return true;
          }
        }
      };
    },
    isEdit() {
      return !!this.$route.params.id && !this.isViewOnly;
    },
    isViewOnly() {
      return this.$route.name === 'ViewShipper';
    },
    pageTitle() {
      if (this.isEdit) {
        return 'Editar embarcador';
      } else {
        return 'Criar embarcador';
      }
    },
    backButtonLabel() {
      return this.isViewOnly ? 'VOLTAR' : 'CANCELAR';
    },
    backButtonColor() {
      return this.isViewOnly ? 'info' : 'error';
    },
    hasValidAddress() {
      const { cep, stateId, cityId, neighborhood, number } = this.addressData;
      const hasNoAddressFields = !cep && !stateId && !cityId && !stateId && !neighborhood && !number;
      const hasAllAddressFields = cep && stateId && cityId && stateId && neighborhood && number;
      if (hasAllAddressFields || hasNoAddressFields) return true;
      return false;
    }
  },

  async created() {
    if (this.isEdit || this.isViewOnly) this.isShipperLoading = true;
    try {
      this.states = await this.fetchStates();
    } catch (error) {
      console.error(error);
    }
    if (this.isEdit || this.isViewOnly) {
      this.isShipperLoading = true;
      try {
        const { isPhysicalPerson, generalData, addressData } = await this.getShipper(this.$route.params.id);
        await this.getCities(addressData.stateId);
        if (isPhysicalPerson) {
          this.selectedType = ShipperTypes.PERSONAL;
        } else {
          this.selectedType = ShipperTypes.COMPANY;
        }
        this.generalData = generalData;
        this.addressData = addressData;
      } catch (error) {
        console.error(error);
      } finally {
        this.isShipperLoading = false;
      }
    }
  },

  methods: {
    ...mapActions('ShippersStore', ['createShipper', 'editShipper', 'getShipper']),
    ...mapActions('AdressStore', ['fetchStates', 'fetchCities']),
    onSubmit() {
      if (this.hasValidAddress) {
        if (this.isEdit) {
          this.edit();
        } else {
          this.create();
        }
      } else {
        this.errors = ['Endereço incompleto! Todos os campos do endereço são obrigatórios, exceto Complemento.'];
        this.errorsType = 'error';
      }
    },
    async create() {
      try {
        this.isSubmitButtonLoading = true;
        let body = {
          accessData: this.accessData,
          addressData: this.addressData,
          generalData: this.generalData,
          isCompany: this.isCompanyShipper
        };

        const data = await this.createShipper(body);

        if (data.success) {
          this.handleSuccessFeedback();
        } else {
          this.errors = data.errors;
          this.errorsType = 'error';
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isSubmitButtonLoading = false;
      }
    },

    async edit() {
      try {
        this.isSubmitButtonLoading = true;
        let body = {
          id: this.$route.params.id,
          accessData: this.accessData,
          addressData: this.addressData,
          generalData: this.generalData,
          isCompany: this.isCompanyShipper
        };

        const data = await this.editShipper(body);

        if (data.success) {
          this.handleEditSuccessFeedback();
        } else {
          this.errors = data.errors;
          this.errorsType = 'error';
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isSubmitButtonLoading = false;
      }
    },

    handleSuccessFeedback() {
      this.errors = ['Embarcador criado com sucesso!'];
      this.errorsType = 'success';
      setTimeout(() => {
        this.$router.back();
      }, 500);
    },
    handleEditSuccessFeedback() {
      this.errors = ['Embarcador editado com sucesso!'];
      this.errorsType = 'success';
      setTimeout(() => {
        this.$router.back();
      }, 500);
    },

    cancel() {
      this.$router.back();
    },

    forceUpdateToRule() {
      this.forceUpdate = this.forceUpdate + 1;
    },

    async getCities(stateId) {
      try {
        this.cities = await this.fetchCities(stateId);
      } catch (error) {
        console.error(error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.v-file-input__text {
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-wrap: wrap;
  top: -7px !important;
  position: relative !important;
  left: 165px !important;
  width: auto !important;
}

::v-deep.v-select.v-select--chips .v-select__selections {
  min-height: 50px;
}
</style>
